import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Control, FieldPath, FieldValues } from 'react-hook-form'

import { fetchBridgeChains, fetchBridgeTokenById } from '@/api/modules'
import GradientWrapper from '@/common/GradientWrapper'
import { Chain } from '@/generated/proto/bridge/models/chain'
import { queryKeys } from '@/queries/keys'
import {
  ControlledUiSelect,
  UiSelectContent,
  UiSelectItem,
  UiSelectTrigger,
  UiSelectValue,
} from '@/ui/UiSelect'

interface TokenInfoSelectProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  tokenId?: string
  onValueChange?: (chainId: string) => void
  onChainSelect?: (chain: Chain) => void
  disabled?: boolean
  placeholder?: string
  /**
   * Mode determines which chains to show:
   * - 'add': show chains where token info can be added (token doesn't have info for this chain yet)
   * - 'remove': show chains where token info already exists (can be removed)
   */
  mode?: 'add' | 'remove'
}

export default function TokenInfoSelect<TFieldValues extends FieldValues>({
  control,
  name,
  tokenId,
  onValueChange,
  onChainSelect,
  disabled = false,
  placeholder = 'Select a chain',
  mode = 'remove',
}: TokenInfoSelectProps<TFieldValues>) {
  const { data: chains = [], isLoading: loadingChains } = useQuery({
    queryKey: queryKeys.chains.list(),
    queryFn: fetchBridgeChains,
  })

  const { data: token, isLoading: loadingToken } = useQuery({
    queryKey: queryKeys.tokens.byId(tokenId),
    queryFn: () => fetchBridgeTokenById(tokenId!),
    enabled: !!tokenId,
  })

  const availableChains = useMemo(() => {
    if (!chains.length) {
      return []
    }

    // If no token selected, show nothing
    if (!token) {
      return []
    }

    const TokenInfoIds = new Set((token.info || []).map(info => info.chainId))

    if (mode === 'add') {
      // Show chains where token info can be added (chains that DON'T have this token yet)
      return chains.filter(chain => !TokenInfoIds.has(chain.id))
    } else {
      // Show chains where token info already exists (can be removed)
      return chains.filter(chain => TokenInfoIds.has(chain.id))
    }
  }, [chains, token, mode])

  const isLoading = loadingChains || loadingToken

  return (
    <ControlledUiSelect
      name={name}
      control={control}
      onValueChange={chainId => {
        onValueChange?.(chainId)
        const selectedChain = availableChains.find(
          chain => chain.id === chainId,
        )
        if (selectedChain) {
          onChainSelect?.(selectedChain)
        }
      }}
      disabled={disabled || isLoading || !tokenId}
    >
      <GradientWrapper>
        <UiSelectTrigger
          size='xl'
          className='bg-input border-border w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
        >
          <UiSelectValue
            placeholder={
              isLoading
                ? 'Loading...'
                : !tokenId
                  ? 'Select a token first'
                  : availableChains?.length === 0
                    ? 'No chains available for this token'
                    : placeholder
            }
          />
        </UiSelectTrigger>
      </GradientWrapper>

      <UiSelectContent>
        {availableChains?.map(chain => (
          <UiSelectItem key={chain.id} value={chain.id}>
            <div className='flex items-center gap-2'>
              <span className='font-medium'>{chain.name}</span>
              <span className='text-muted-foreground text-xs'>
                ({chain.id})
              </span>
            </div>
          </UiSelectItem>
        ))}
      </UiSelectContent>
    </ControlledUiSelect>
  )
}
